import { Flex, Button } from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import { FontFamily } from '@common/theming/Fonts';
import currency from '@common/utility/currency';
import React, { ChangeEvent, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import SplitView from 'src/apps/invest/common/shells/SplitView';
import { ROUTES } from 'src/apps/invest/config';
import PaymentSourceSelector from 'src/apps/invest/common/components/PaymentSourceSelector';
import PreflightModal from 'src/apps/invest/common/components/PreflightModal';
import { useCheckoutSFRViewModel } from '../../models/CheckoutSFRViewModel';
import { InvestPreflightHint } from '@services/appsync/types';
import Amount from '../components/Amount';
import { Header } from '../components/Header';
import { Section } from '../components/Section';
import Summary from '../components/Summary';
import { Helmet } from 'react-helmet';

export const CheckoutSFRAmountPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [preflightHint, setPreflightHint] = useState<InvestPreflightHint | null>(null);
  const { id } = useParams();
  const viewModel = useCheckoutSFRViewModel();
  const form = useForm<{ amount: number }>({
    defaultValues: {
      amount: viewModel.amount,
    },
  });
  const amountWatch = useWatch({ control: form.control, name: 'amount' });
  const investorModel = useInvestorAccountModel();
  const thumbnail = viewModel.property?.sfr?.info?.images?.[0];
  const sharesSelected = (viewModel.amount ?? 0) / Number(viewModel.property?.share_price ?? 0);

  const handleFormSubmission = form.handleSubmit(async () => {
    setIsLoading(true);
    const result = await viewModel.handleAmountFormSubmit();

    // Guide the user if the preflight check returned any hints.
    if (!result.success && result.preflightHints && result.preflightHints.length > 0) {
      const firstHint = result.preflightHints[0];
      setPreflightHint(firstHint ?? null)
    } else if (!result.success) {
      form.setError('amount', { message: "Something went wrong. Please try again and contact us if you're still having trouble." });
    }

    setIsLoading(false);
  });

  const handleSuggestedAmount = (suggestedAmount: number) => {
    const amount = parseInt(suggestedAmount.toString());
    form.setValue('amount', amount);
    form.clearErrors('amount');
    viewModel.setAmount(amount);
  };

  const handleAgreementChange = (e: ChangeEvent<HTMLInputElement>) => {
    viewModel.setAmountAgreement(e.target.checked);
  };

  return (
    <SplitView.UI columnRatio={60}>
      <SplitView.LeftColumn>
        <Helmet>
          <title>Invest in {viewModel.property?.display_name ?? ''} | Concreit</title>
        </Helmet>
        {preflightHint && <PreflightModal preflightHint={preflightHint} onClose={() => setPreflightHint(null)} />}
        <Amount.Form onSubmit={handleFormSubmission}>
          <Header
            breadCrumb={
              <Button
                as={Link}
                to={ROUTES.propertyDetail(id ?? '')}
                color="black"
                fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                fontWeight="normal"
                fontSize={['16px', '20px']}
                variant="ghost"
                leftIcon={<Icon icon="arrow-left-long" boxSize="16px" variant="regular" />}>
                Back
              </Button>
            }
            image={thumbnail?.url}
            title={`Invest in ${viewModel.property?.display_name}`}
          />
          <Section title="Investment Amount" description="Please enter the amount you would like to invest.">
            <Amount.AmountField form={form} />
            {investorModel.sfrEligibility?.eligible === false ? (
              <Amount.Eligibility />
            ) : (
              <>
                <Amount.AutoSuggestions form={form} onSuggestedAmount={handleSuggestedAmount} />
              </>
            )}
          </Section>
        </Amount.Form>
        <Section
          icon="lock"
          title="Payment Source"
          description="Please select the payment source to use.">
          <PaymentSourceSelector />
        </Section>
      </SplitView.LeftColumn>
      <SplitView.RightColumn gap={'31px'}>
        <Summary.Container label="Purchase Summary">
          <Summary.Item
            label={viewModel.property?.display_name ?? ''}
            description={`${sharesSelected} Shares`}
            image={thumbnail?.url}
            value={currency.format(amountWatch ?? 0)}
          />
        </Summary.Container>
        <Flex padding="16px">
          <Amount.Agreement onChange={handleAgreementChange} />
        </Flex>
        <Amount.Form onSubmit={handleFormSubmission}>
          <Amount.SubmitButton
            isDisabled={
              !amountWatch ||
              !viewModel.amountAgreement ||
              amountWatch === 0 ||
              Boolean(form.formState.errors.amount) ||
              investorModel.sfrEligibility?.eligible === false
            }
            isLoading={isLoading}
          />
        </Amount.Form>
      </SplitView.RightColumn>
    </SplitView.UI>
  );
};
